"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import ReCaptcha, { type ReCAPTCHA } from "react-google-recaptcha";
import { Controller, useForm, useWatch } from "react-hook-form";
import { isValidPhoneNumber } from "react-phone-number-input";
import { toast } from "sonner";
import * as z from "zod";

import { postUserHelp } from "#app/_api/external-bol-registration-service/post-user-help";
import { CardHeaderWithIconTitle } from "#app/_ui/components/cards/CardHeaderWithIconTitle";
import { SuccessDialog } from "#app/_ui/components/dialogs/SuccessDialog";
import { ENV } from "#app/env";
import { logError } from "#app/lib/logger";
import { Button } from "#ui/button";
import { Card, CardContent, type CardRootProps } from "#ui/card";
import { Field } from "#ui/field";
import { FieldLabel } from "#ui/field-label";
import { FieldMessage } from "#ui/field-message";
import { PhoneInput } from "#ui/phone-input";
import { TextInput } from "#ui/text-input";
import { Textarea } from "#ui/textarea";
import { P } from "#ui/typography";

const CardSendMessageUnauthedFormSchema = z
  .object({
    emailAddress: z.string().optional(),
    phoneNumber: z.string().optional(),
    message: z
      .string()
      .min(1, {
        error: "Message is required.",
      })
      .max(3000, {
        error: "Character limit exceeded.",
      }),
  })
  .check((ctx) => {
    if (!ctx.value.emailAddress && !ctx.value.phoneNumber) {
      ctx.issues.push({
        code: "custom",
        input: ctx.value.emailAddress,
        message: "Either Email or Phone Number is required.",
        path: ["emailAddress"],
      });
      ctx.issues.push({
        code: "custom",
        input: ctx.value.phoneNumber,
        message: "Either Email or Phone Number is required.",
        path: ["phoneNumber"],
      });
      return z.NEVER;
    }

    if (
      ctx.value.emailAddress &&
      !z.email().safeParse(ctx.value.emailAddress).success
    ) {
      ctx.issues.push({
        code: "custom",
        input: ctx.value.emailAddress,
        message: "Email is invalid.",
        path: ["emailAddress"],
      });
    }

    if (ctx.value.phoneNumber && !isValidPhoneNumber(ctx.value.phoneNumber)) {
      ctx.issues.push({
        code: "custom",
        input: ctx.value.phoneNumber,
        message: "Phone number is invalid.",
        path: ["phoneNumber"],
      });
    }
  });

type CardSendMessageUnauthedFormValues = z.infer<
  typeof CardSendMessageUnauthedFormSchema
>;

export function CardSendMessageUnauthed(props: CardRootProps) {
  const [open, setOpen] = useState(false);

  const ref = useRef<ReCAPTCHA | null>(null);

  const {
    control,
    formState: { errors },
    handleSubmit,
    register,
    reset,
    trigger,
  } = useForm<CardSendMessageUnauthedFormValues>({
    defaultValues: {
      emailAddress: "",
      phoneNumber: "",
      message: "",
    },
    resolver: zodResolver(CardSendMessageUnauthedFormSchema),
    mode: "onChange",
    reValidateMode: "onChange",
  });

  const emailValue = useWatch({ control, name: "emailAddress" });
  const phoneValue = useWatch({ control, name: "phoneNumber" });
  const messageValue = useWatch({ control, name: "message" });

  useEffect(() => {
    // When either field changes, trigger validation on both
    // so that errors can be both shown and cleared immediately.
    if (emailValue || phoneValue) {
      void trigger(["emailAddress", "phoneNumber"]);
    }
  }, [emailValue, phoneValue, trigger]);

  const { mutate, isPending } = useMutation({
    mutationFn: postUserHelp,
    onSuccess: () => {
      reset();
      ref.current?.reset();
      setOpen(true);
    },
    onError: (error) => {
      logError(error);
      toast.error("Something went wrong");
    },
  });

  return (
    <>
      <Card {...props}>
        <CardHeaderWithIconTitle
          iconName="baird-mail"
          title="Send Us a Message"
        />
        <CardContent>
          <form
            id="authed-contact-us-form"
            onSubmit={(event) => {
              event.preventDefault();
              void handleSubmit((data) => {
                const captchaResponse = ref.current?.getValue();
                if (!captchaResponse) {
                  toast.error("Please complete the captcha.");
                  return;
                }
                mutate({ body: { ...data, captchaResponse } });
              })(event);
            }}
            className="grid gap-20px"
          >
            <P size="sm">
              Send our Baird Online team a message. Messages will be answered
              within 2&ndash;3 business days.
            </P>

            <Field>
              <FieldLabel htmlFor="emailAddress">Email</FieldLabel>
              <TextInput
                {...register("emailAddress")}
                id="emailAddress"
                autoComplete="email"
                aria-invalid={errors.emailAddress?.message ? "true" : undefined}
                aria-describedby={
                  errors.emailAddress?.message
                    ? "emailAddress-error"
                    : undefined
                }
              />
              <FieldMessage id="emailAddress-error" intent="error">
                {errors.emailAddress?.message}
              </FieldMessage>
            </Field>

            <Field>
              <FieldLabel htmlFor="phoneNumber">Phone Number</FieldLabel>
              <Controller
                control={control}
                name="phoneNumber"
                render={({ field }) => (
                  <PhoneInput
                    {...field}
                    id="phoneNumber"
                    autoComplete="tel"
                    aria-invalid={
                      errors.phoneNumber?.message ? "true" : undefined
                    }
                    aria-describedby={
                      errors.phoneNumber?.message
                        ? "phoneNumber-error"
                        : undefined
                    }
                  />
                )}
              />
              <FieldMessage id="phoneNumber-error" intent="error">
                {errors.phoneNumber?.message}
              </FieldMessage>
            </Field>

            <Field>
              <FieldLabel htmlFor="message">Message</FieldLabel>
              <Textarea
                {...register("message")}
                id="message"
                placeholder="Include a note here"
                aria-invalid={errors.message?.message ? "true" : undefined}
                aria-describedby={
                  errors.message?.message
                    ? ["message-description", "message-error"].join(" ")
                    : "message-description"
                }
                className="min-h-120px"
              />
              <FieldMessage
                id="message-description"
                className={
                  errors.message?.message?.includes("exceeded")
                    ? "text-error-100"
                    : "text-shade-80"
                }
              >
                {messageValue.length.toLocaleString()}/3,000 characters
              </FieldMessage>
              <FieldMessage id="message-error" intent="error">
                {errors.message?.message}
              </FieldMessage>
            </Field>

            <ReCaptcha
              id="captchaResponse"
              sitekey={ENV.recaptchaSiteKey || ""}
              className="max-w-[275px] overflow-visible"
              ref={ref}
            />

            <div>
              <Button
                type="submit"
                className="w-full"
                pending={isPending}
                disabled={isPending}
              >
                Send message
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <SuccessDialog
        title="Success!"
        text="Your message has been sent."
        proceed={() => setOpen(false)}
        open={open}
      />
    </>
  );
}
